"use client";

import { cn } from "@humanex/ui/libs";
import React, { useState } from "react";

// Utility function to get nested object values using dot notation
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return null;

  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Base interfaces
export interface TableColumn<T = any> {
  key: string;
  title: string;
  dataIndex?: keyof T | string;
  width?: string | number;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  filterable?: boolean;
  formatter?: (value: any, record: T, index: number) => any;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface TableProps<T = any> extends React.HTMLAttributes<HTMLDivElement> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  emptyText?: string;
  className?: string;
  rowKey?: keyof T | ((record: T) => string | number);
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    onChange?: (page: number, pageSize: number) => void;
  };
  sortConfig?: {
    key: string;
    direction: "asc" | "desc";
  };
  onSort?: (key: string, direction: "asc" | "desc") => void;
  selection?: {
    selectedRowKeys: (string | number)[];
    onChange: (selectedRowKeys: (string | number)[], selectedRows: T[]) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean };
  };
  size?: "small" | "middle" | "large";
  bordered?: boolean;
  striped?: boolean;
  hoverable?: boolean;
}

// Table Header Component
export interface TableHeaderProps<T = any> {
  columns: TableColumn<T>[];
  sortConfig?: { key: string; direction: "asc" | "desc" };
  onSort?: (key: string, direction: "asc" | "desc") => void;
  selection?: {
    selectedRowKeys: (string | number)[];
    onChange: (selectedRowKeys: (string | number)[], selectedRows: T[]) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean };
  };
  data: T[];
  size?: "small" | "middle" | "large";
}

export const TableHeader = <T,>({
  columns,
  sortConfig,
  onSort,
  selection,
  data,
  size = "middle"
}: TableHeaderProps<T>) => {
  const handleSort = (key: string) => {
    if (!onSort) return;

    const direction = sortConfig?.key === key && sortConfig?.direction === "asc" ? "desc" : "asc";
    onSort(key, direction);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!selection) return;

    const allKeys = data.map((record, index) =>
      typeof record === "object" && record !== null ? (record as any).id || index : index
    );

    selection.onChange(checked ? allKeys : [], checked ? data : []);
  };

  const isAllSelected =
    selection &&
    data.length > 0 &&
    data.every((record, index) => {
      const key = typeof record === "object" && record !== null ? (record as any).id || index : index;
      return selection.selectedRowKeys.includes(key);
    });

  const sizeClasses = {
    small: "px-2 py-1 text-xs",
    middle: "px-4 py-3 text-xs font-medium uppercase tracking-wide",
    large: "px-6 py-4 text-sm"
  };

  return (
    <thead className="bg-gray-50">
      <tr>
        {selection && (
          <th className={cn("border-b border-gray-100 text-left", sizeClasses[size])}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded border-gray-200"
            />
          </th>
        )}
        {columns.map((column, index) => (
          <th
            key={column.key}
            className={cn(
              "border-b border-gray-100 text-left text-gray-600",
              index === 0 && "rounded-tl-xl",
              index === columns.length - 1 && "rounded-tr-xl",
              sizeClasses[size],
              column.align === "center" && "text-center",
              column.align === "right" && "text-right",
              column.sortable && "cursor-pointer hover:bg-gray-100 select-none",
              column.headerClassName
            )}
            style={{ width: column.width }}
            onClick={() => column.sortable && handleSort(column.key)}
          >
            <div className="flex items-center gap-1">
              <span>{column.title}</span>
              {column.sortable && (
                <div className="flex flex-col">
                  <svg
                    className={cn(
                      "w-3 h-3 transition-colors",
                      sortConfig?.key === column.key && sortConfig?.direction === "asc"
                        ? "text-ink"
                        : "text-gray-500"
                    )}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );
};

// Table Body Component
export interface TableBodyProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  rowKey?: keyof T | ((record: T) => string | number);
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  selection?: {
    selectedRowKeys: (string | number)[];
    onChange: (selectedRowKeys: (string | number)[], selectedRows: T[]) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean };
  };
  loading?: boolean;
  emptyText?: string;
  size?: "small" | "middle" | "large";
  striped?: boolean;
  hoverable?: boolean;
}

export const TableBody = <T,>({
  data,
  columns,
  rowKey,
  rowClassName,
  onRow,
  selection,
  loading = false,
  emptyText = "No data",
  size = "middle",
  striped = false,
  hoverable = true
}: TableBodyProps<T>) => {
  const getRowKey = (record: T, index: number): string | number => {
    if (typeof rowKey === "function") {
      return rowKey(record);
    }
    if (typeof rowKey === "string" && record && typeof record === "object") {
      return (record as any)[rowKey] || index;
    }
    return index;
  };

  const handleRowSelect = (record: T, index: number, checked: boolean) => {
    if (!selection) return;

    const key = getRowKey(record, index);
    const newSelectedKeys = checked
      ? [...selection.selectedRowKeys, key]
      : selection.selectedRowKeys.filter((k) => k !== key);

    const newSelectedRows = data.filter((_, i) => newSelectedKeys.includes(getRowKey(_, i)));

    selection.onChange(newSelectedKeys, newSelectedRows);
  };

  const sizeClasses = {
    small: "px-2 py-1 text-xs",
    middle: "px-4 py-3 text-sm",
    large: "px-6 py-4 text-base"
  };

  if (loading) {
    return (
      <tbody>
        <tr className={cn("bg-white")}>
          <td
            colSpan={columns.length + (selection ? 1 : 0)}
            className={cn("text-center text-gray-500 rounded-b-xl", sizeClasses[size], "py-8")}
          >
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400"></div>
              Loading...
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  if (data.length === 0) {
    return (
      <tbody>
        <tr className={cn("bg-white")}>
          <td
            colSpan={columns.length + (selection ? 1 : 0)}
            className={cn("text-center text-gray-500 rounded-b-xl", sizeClasses[size], "py-8")}
          >
            {emptyText}
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((record, rIndex) => {
        const key = getRowKey(record, rIndex);
        const isSelected = selection?.selectedRowKeys.includes(key) || false;
        const rowProps = onRow?.(record, rIndex) || {};
        const checkboxProps = selection?.getCheckboxProps?.(record) || {};

        const computedRowClassName =
          typeof rowClassName === "function" ? rowClassName(record, rIndex) : rowClassName;

        return (
          <tr
            key={key}
            className={cn(
              "bg-white border-b border-gray-100 transition-colors rounded-xl",
              striped && rIndex % 2 === 1 && "bg-gray-50",
              hoverable && "hover:bg-surface",
              isSelected && "bg-secondary-50",
              computedRowClassName
            )}
            {...rowProps}
          >
            {selection && (
              <td className={cn(sizeClasses[size], "rounded-bl-xl")}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={checkboxProps.disabled}
                  onChange={(e) => handleRowSelect(record, rIndex, e.target.checked)}
                  className="rounded border-gray-200 disabled:opacity-40"
                />
              </td>
            )}
            {columns.map((column, cIndex) => {
              let value = column.dataIndex ? getNestedValue(record, column.dataIndex as string) : null;

              // Apply formatter if provided
              if (column.formatter && value !== null) {
                value = column.formatter(value, record, cIndex);
              }

              const cellContent = column.render ? column.render(value, record, cIndex) : value;

              return (
                <td
                  key={column.key}
                  className={cn(
                    "text-ink",
                    !selection && rIndex === data.length - 1 && cIndex === 0 && "rounded-bl-xl",
                    rIndex === data.length - 1 && cIndex === columns.length - 1 && "rounded-br-xl",
                    sizeClasses[size],
                    column.align === "center" && "text-center",
                    column.align === "right" && "text-right",
                    column.className
                  )}
                >
                  {cellContent}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

// Pagination Component
export interface PaginationProps {
  current: number;
  pageSize: number;
  total: number;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  onChange?: (page: number, pageSize: number) => void;
  className?: string;
}

export const Pagination = ({
  current,
  pageSize,
  total,
  showSizeChanger = false,
  showQuickJumper = false,
  onChange,
  className
}: PaginationProps) => {
  const totalPages = Math.ceil(total / pageSize);
  const [jumpPage, setJumpPage] = useState("");

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && onChange) {
      onChange(page, pageSize);
    }
  };

  const handleSizeChange = (newPageSize: number) => {
    if (onChange) {
      onChange(1, newPageSize);
    }
  };

  const handleJump = () => {
    const page = parseInt(jumpPage);
    if (page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setJumpPage("");
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, current - 2);
      const end = Math.min(totalPages, start + maxVisible - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className={cn("flex items-center justify-between mt-4", className)}>
      <div className="text-sm text-gray-500">
        Showing {(current - 1) * pageSize + 1} to {Math.min(current * pageSize, total)} of {total} entries
      </div>

      <div className="flex items-center gap-2">
        {showSizeChanger && (
          <select
            value={pageSize}
            onChange={(e) => handleSizeChange(parseInt(e.target.value))}
            className="px-2 py-1 border border-gray-200 rounded-lg text-sm text-ink bg-white focus:border-secondary-400 focus:border-[1.5px]"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}

        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(current - 1)}
            disabled={current === 1}
            className="px-2 py-1 border border-gray-200 rounded-lg text-sm text-ink hover:bg-surface disabled:opacity-40 disabled:pointer-events-none"
          >
            Previous
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={cn(
                "px-3 py-1 border border-gray-200 rounded-lg text-sm text-ink hover:bg-surface",
                page === current && "bg-primary-500 text-white border-primary-500 hover:bg-primary-600"
              )}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(current + 1)}
            disabled={current === totalPages}
            className="px-2 py-1 border border-gray-200 rounded-lg text-sm text-ink hover:bg-surface disabled:opacity-40 disabled:pointer-events-none"
          >
            Next
          </button>
        </div>

        {showQuickJumper && (
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-500">Go to</span>
            <input
              type="number"
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJump()}
              className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-ink bg-white focus:border-secondary-400 focus:border-[1.5px]"
              min="1"
              max={totalPages}
            />
            <button
              onClick={handleJump}
              className="px-2 py-1 border border-gray-200 rounded-lg text-sm text-ink hover:bg-surface"
            >
              Go
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Table Component
export const Table = <T,>({
  data,
  columns,
  loading = false,
  emptyText = "No data",
  className,
  rowKey,
  rowClassName,
  onRow,
  pagination,
  sortConfig,
  onSort,
  selection,
  size = "middle",
  bordered = false,
  striped = false,
  hoverable = true,
  ...props
}: TableProps<T>) => {
  const tableClasses = cn(
    "overflow-visible w-full border-collapse",
    bordered && "border border-gray-200",
    className
  );

  return (
    <div className="w-full" {...props}>
      <div className="w-full overflow-visible relative">
        <table className={tableClasses}>
          <TableHeader
            columns={columns}
            sortConfig={sortConfig}
            onSort={onSort}
            selection={selection}
            data={data}
            size={size}
          />
          <TableBody
            data={data}
            columns={columns}
            rowKey={rowKey}
            rowClassName={rowClassName}
            onRow={onRow}
            selection={selection}
            loading={loading}
            emptyText={emptyText}
            size={size}
            striped={striped}
            hoverable={hoverable}
          />
        </table>
      </div>

      {pagination && (
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger={pagination.showSizeChanger}
          showQuickJumper={pagination.showQuickJumper}
          onChange={pagination.onChange}
        />
      )}
    </div>
  );
};

// Export all components
export default Table;
